import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Avatar,
  IconButton,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Grid,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
   PersonAdd as PersonAddIcon,
   Phone as PhoneIcon,
   Refresh as RefreshIcon,
   ArrowForward as ArrowForwardIcon,
   CheckCircle as CheckCircleIcon,
   Cancel as CancelIcon,
   HourglassEmpty,
   Send as SendIcon,
   Delete as DeleteIcon,
   Search as SearchIcon,
   MoreVert as MoreVertIcon,
   FilterList as FilterListIcon,
   Email as EmailIcon,
   Badge,
   Info as InfoIcon,
   KeyboardArrowDown as ExpandMoreIcon,
   KeyboardArrowUp as ExpandLessIcon,
   PhotoLibrary as PhotoIcon,
   People as PeopleIcon,
   Share as ShareIcon,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useResponsive, useResponsiveTypography, useResponsiveSpacing } from '../utils/responsive';
import {
   collection,
   query,
   where,
   getDocs,
   orderBy,
   Timestamp,
   doc,
   updateDoc,
   deleteDoc,
   serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { resendInvitation } from '../services/tripParticipantService';
import { resendEventInvitation } from '../services/eventInvitationService';
import { cancelTripInvitation, cancelEventInvitation } from '../services/invitationService';
import SendInvitationDialog from '../components/invitations/SendInvitationDialog';
import SendEventInvitationDialog from '../components/invitations/SendEventInvitationDialog';
import { useSubscription } from '../contexts/SubscriptionContext';
// Interface for the invitation object
interface Invitation {
   id: string;
   tripId?: string;
   eventId?: string;
   albumId?: string;
   tripTitle?: string;
   eventTitle?: string;
   albumTitle?: string;
   email: string;
   phoneNumber?: string;
   method?: 'email' | 'sms';
   status: 'pending' | 'accepted' | 'declined';
   createdAt: Date;
   expiresAt: Date;
   acceptedAt?: Date | null;
   declinedAt?: Date | null;
   role?: string;
   experienceType: 'trip' | 'event' | 'album';
   inviterName?: string;
   recipientName?: string;
}

// Tab interface
interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

const TabPanel = (props: TabPanelProps) => {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`invitation-tabpanel-${index}`}
         aria-labelledby={`invitation-tab-${index}`}
         {...other}
      >
         {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
      </div>
   );
};

const InvitationManagement: React.FC = () => {
   const { currentUser } = useUser();
   const navigate = useNavigate();

   // Add responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();
   const [invitations, setInvitations] = useState<Invitation[]>([]);
   const [filteredInvitations, setFilteredInvitations] = useState<Invitation[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [searchTerm, setSearchTerm] = useState('');
   const [tabValue, setTabValue] = useState(0);
   const [refreshing, setRefreshing] = useState(false);
   const [resendingId, setResendingId] = useState<string | null>(null);
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
   const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
   const [sendInvitationDialogOpen, setSendInvitationDialogOpen] = useState(false);
   const [invitationType, setInvitationType] = useState<'all' | 'trips' | 'events'>('all');
   const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'declined'>(
      'all'
   );
   const [successMessage, setSuccessMessage] = useState<string | null>(null);
   const { checkSubscriptionStatus } = useSubscription();
   const [guideSectionExpanded, setGuideSectionExpanded] = useState(false);

   // Menu handling
   const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invitation: Invitation) => {
      setAnchorEl(event.currentTarget);
      setSelectedInvitation(invitation);
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
   };

   // Tab handling
   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   // Filter invitations based on tab and search
   useEffect(() => {
      if (!invitations.length) {
         setFilteredInvitations([]);
         return;
      }

      let filtered = [...invitations];

      // Apply tab filter
      if (tabValue === 1) {
         filtered = filtered.filter((inv) => inv.status === 'pending');
      } else if (tabValue === 2) {
         filtered = filtered.filter((inv) => inv.status === 'accepted');
      } else if (tabValue === 3) {
         filtered = filtered.filter((inv) => inv.status === 'declined');
      }

      // Apply invitation type filter
      if (invitationType === 'trips') {
         filtered = filtered.filter((inv) => inv.experienceType === 'trip');
      } else if (invitationType === 'events') {
         filtered = filtered.filter((inv) => inv.experienceType === 'event');
      }

      // Apply search filter
      if (searchTerm) {
         const search = searchTerm.toLowerCase();
         filtered = filtered.filter(
            (inv) =>
               inv.email.toLowerCase().includes(search) ||
               inv.tripTitle?.toLowerCase().includes(search) ||
               inv.eventTitle?.toLowerCase().includes(search) ||
               inv.albumTitle?.toLowerCase().includes(search) ||
               (inv.phoneNumber && inv.phoneNumber.includes(search))
         );
      }

      setFilteredInvitations(filtered);
   }, [invitations, tabValue, searchTerm, invitationType]);

   // Migrate viewer roles to editor roles
   const migrateRoles = async () => {
      if (!currentUser) return;

      try {
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/invitations/migrate-roles/${currentUser.uid}`,
            {
               method: 'POST',
               headers,
            }
         );

         if (response.ok) {
            const data = await response.json();
            if (data.updatedCount > 0) {
               // Migration completed
            }
         }
      } catch (err) {
         console.warn('Role migration failed (non-critical):', err);
      }
   };

   // Fetch invitation data
   const fetchInvitations = async () => {
      if (!currentUser) return;

      try {
         setLoading(true);

         // First, try to migrate any old viewer roles to editor
         await migrateRoles();

         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/invitations/management/${currentUser.uid}`,
            {
               method: 'GET',
               headers,
            }
         );

         if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch invitations`);
         }

         const data = await response.json();

         if (!data.success) {
            throw new Error(data.error || 'Failed to fetch invitations');
         }

         // Process the invitation data
         const invitationList: Invitation[] = data.invitations.map((inv: any) => ({
            id: inv.id,
            tripId: inv.tripId,
            eventId: inv.eventId,
            albumId: inv.albumId,
            tripTitle: inv.tripTitle,
            eventTitle: inv.eventTitle,
            albumTitle: inv.albumTitle,
            email: inv.email,
            phoneNumber: inv.phoneNumber,
            method: inv.method,
            status: inv.status,
            role: inv.role || 'editor', // Ensure editor as default
            experienceType: inv.experienceType,
            createdAt: new Date(inv.createdAt),
            expiresAt: new Date(inv.expiresAt),
            acceptedAt: inv.acceptedAt ? new Date(inv.acceptedAt) : null,
            declinedAt: inv.declinedAt ? new Date(inv.declinedAt) : null,
            inviterName: inv.inviterName,
            recipientName: inv.recipientName,
         }));

         setInvitations(invitationList);
         setError(null);
      } catch (err) {
         console.error('Error fetching invitations:', err);
         setError('Failed to load invitation data');
      } finally {
         setLoading(false);
         setRefreshing(false);
      }
   };

   // Load invitations on mount
   useEffect(() => {
      fetchInvitations();
   }, [currentUser]);

   // Refresh invitations when page becomes visible (to catch status updates)
   useEffect(() => {
      const handleVisibilityChange = () => {
         if (!document.hidden && currentUser && !resendingId) {
            fetchInvitations();
         }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
   }, [currentUser, resendingId]);

   // Periodic refresh to catch status updates (every 30 seconds)
   useEffect(() => {
      if (!currentUser) return;

      const interval = setInterval(() => {
         // Don't refresh if resend is in progress
         if (!resendingId) {
            fetchInvitations();
         }
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
   }, [currentUser, resendingId]);

   // Handle refresh invitations
   const handleRefresh = () => {
      // Don't refresh if resend is in progress
      if (resendingId) return;

      setRefreshing(true);
      fetchInvitations();
   };

   // Handle resend invitation
   const handleResendInvite = async (invitationId: string) => {
      if (!selectedInvitation) return;

      setResendingId(invitationId);
      handleMenuClose(); // Close menu immediately when clicked

      try {
         // Check if this is a phone invitation
         if (selectedInvitation.method === 'sms') {
            // For phone invitations, we need to call the server API directly
            const { getAuthHeaders } = await import('../utils/auth');
            const headers = await getAuthHeaders();
            
            const endpoint = selectedInvitation.experienceType === 'trip' 
               ? '/api/trip/resend-phone-invite'
               : '/api/event/resend-phone-invite';
            
            const response = await fetch(endpoint, {
               method: 'POST',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ invitationId }),
            });
            
            if (!response.ok) {
               const errorData = await response.json();
               throw new Error(errorData.error || 'Failed to resend phone invitation');
            }
         } else {
            // Handle email invitations as before
            if (selectedInvitation.experienceType === 'trip') {
               await resendInvitation(invitationId);
            } else {
               await resendEventInvitation(invitationId);
            }
         }

         setSuccessMessage('Invitation resent successfully!');

         // Refresh the list after a short delay
         setTimeout(() => {
            fetchInvitations();
            setSuccessMessage(null);
         }, 2000);
      } catch (error) {
         console.error('Error resending invitation:', error);
         setError('Failed to resend invitation');

         // Clear error after a short delay
         setTimeout(() => {
            setError(null);
         }, 3000);
      } finally {
         setResendingId(null);
      }
   };

   // Handle delete invitation
   const handleDeleteInvite = async () => {
      if (!selectedInvitation) return;

      setLoading(true);
      setOpenDeleteDialog(false);

      try {
         const invitation = selectedInvitation;

         // Call the appropriate service function based on invitation type
         if (invitation.experienceType === 'trip' && invitation.tripId) {
            await cancelTripInvitation(invitation.tripId, invitation.email);
         } else if (invitation.experienceType === 'event' && invitation.eventId) {
            await cancelEventInvitation(invitation.eventId, invitation.email);
         } else {
            throw new Error('Invalid invitation type or missing ID');
         }

         // Update the local state
         setInvitations((prevInvitations) =>
            prevInvitations.filter((inv) => inv.id !== invitation.id)
         );

         setSuccessMessage('Invitation deleted successfully and participant removed!');

         // Clear message after a delay
         setTimeout(() => {
            setSuccessMessage(null);
         }, 2000);
      } catch (error) {
         console.error('Error deleting invitation:', error);
         setError(
            `Failed to delete invitation: ${error instanceof Error ? error.message : 'Unknown error'}`
         );

         // Clear error after a delay
         setTimeout(() => {
            setError(null);
         }, 3000);
      } finally {
         setLoading(false);
         setSelectedInvitation(null);
      }
   };

   // Navigate to a specific trip
   const handleViewTrip = (tripId: string) => {
      navigate(`/trip/${tripId}`);
   };

   // Format date to be more readable
   const formatDate = (date: Date) => {
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
         return 'Today';
      } else if (diffDays === 1) {
         return 'Yesterday';
      } else if (diffDays < 7) {
         return `${diffDays} days ago`;
      } else {
         return date.toLocaleDateString();
      }
   };

   // Check if an invitation is expired
   const isExpired = (expiryDate: Date) => {
      return new Date() > expiryDate;
   };

   // Count invitations by status
   const pendingCount = invitations.filter((inv) => inv.status === 'pending').length;
   const acceptedCount = invitations.filter((inv) => inv.status === 'accepted').length;
   const declinedCount = invitations.filter((inv) => inv.status === 'declined').length;

   // Render invitation list item
   const renderInvitationItem = (invitation: Invitation) => {
      return (
         <ListItem
            key={invitation.id}
            alignItems='flex-start'
            secondaryAction={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {invitation.status === 'pending' && (
                     <Tooltip title='View Details'>
                        <IconButton
                           edge='end'
                           onClick={() => handleNavigateToItem(invitation)}
                           size={isMobile ? 'medium' : 'small'}
                           sx={{
                              minHeight: isMobile ? '40px' : '32px',
                              minWidth: isMobile ? '40px' : '32px',
                           }}
                        >
                           <ArrowForwardIcon />
                        </IconButton>
                     </Tooltip>
                  )}
                  <IconButton
                     edge='end'
                     onClick={(e) => handleMenuOpen(e, invitation)}
                     size={isMobile ? 'medium' : 'small'}
                     sx={{
                        minHeight: isMobile ? '40px' : '32px',
                        minWidth: isMobile ? '40px' : '32px',
                     }}
                  >
                     <MoreVertIcon />
                  </IconButton>
               </Box>
            }
            sx={{
               mb: isMobile ? 0.5 : 1,
               borderRadius: isMobile ? 0.5 : 1,
               bgcolor: 'background.paper',
               '&:hover': { bgcolor: 'action.hover' },
               border: '1px solid',
               borderColor: 'divider',
               py: isMobile ? 1 : 1.5,
            }}
         >
            <ListItemAvatar>
               <Avatar
                  sx={{
                     bgcolor:
                        invitation.status === 'pending'
                           ? 'warning.main'
                           : invitation.status === 'accepted'
                             ? 'success.main'
                             : 'error.main',
                     width: isMobile ? 40 : 48,
                     height: isMobile ? 40 : 48,
                  }}
               >
                  {invitation.status === 'pending' ? (
                     <HourglassEmpty />
                  ) : invitation.status === 'accepted' ? (
                     <CheckCircleIcon />
                  ) : (
                     <CancelIcon />
                  )}
               </Avatar>
            </ListItemAvatar>
            <ListItemText
               primary={
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: isMobile ? 0.5 : 1,
                     }}
                  >
                     <Typography
                        variant='subtitle1'
                        sx={{
                           fontSize: isMobile ? fontSize.body1 : fontSize.subtitle1,
                        }}
                     >
                        {invitation.method === 'sms' && invitation.phoneNumber 
                           ? invitation.phoneNumber 
                           : invitation.email}
                     </Typography>
                     <Chip
                        size={isMobile ? 'small' : 'small'}
                        label={invitation.method === 'email' ? 'Email' : 'SMS'}
                        icon={
                           invitation.method === 'email' ? (
                              <EmailIcon fontSize='small' />
                           ) : (
                              <PhoneIcon fontSize='small' />
                           )
                        }
                        color='default'
                        variant='outlined'
                        sx={{
                           fontSize: isMobile ? '0.7rem' : '0.75rem',
                           height: isMobile ? '20px' : '24px',
                        }}
                     />
                     <Chip
                        size={isMobile ? 'small' : 'small'}
                        label={
                           invitation.experienceType === 'trip'
                              ? 'Trip'
                              : invitation.experienceType === 'event'
                                ? 'Event'
                                : 'Album'
                        }
                        color={
                           invitation.experienceType === 'trip'
                              ? 'info'
                              : invitation.experienceType === 'event'
                                ? 'secondary'
                                : 'warning'
                        }
                        sx={{
                           fontSize: isMobile ? '0.7rem' : '0.75rem',
                           height: isMobile ? '20px' : '24px',
                        }}
                     />
                     <Chip
                        size={isMobile ? 'small' : 'small'}
                        label={invitation.role}
                        color='primary'
                        sx={{
                           fontSize: isMobile ? '0.7rem' : '0.75rem',
                           height: isMobile ? '20px' : '24px',
                        }}
                     />
                  </Box>
               }
               secondary={
                  <>
                     <Typography
                        sx={{ display: 'block' }}
                        component='span'
                        variant='body2'
                        color='text.primary'
                     >
                        {invitation.experienceType === 'trip'
                           ? invitation.tripTitle
                           : invitation.experienceType === 'event'
                             ? invitation.eventTitle
                             : invitation.albumTitle}
                     </Typography>
                     <Typography
                        sx={{ display: 'block' }}
                        component='span'
                        variant='body2'
                        color='text.secondary'
                     >
                        Sent {formatDate(invitation.createdAt)}
                        {invitation.status === 'pending' && !isExpired(invitation.expiresAt) && (
                           <>
                              {` • Expires ${formatDate(invitation.expiresAt)}`}
                              <Chip size='small' label='Pending' color='warning' sx={{ ml: 1 }} />
                           </>
                        )}
                        {invitation.status === 'pending' && isExpired(invitation.expiresAt) && (
                           <Chip size='small' label='Expired' color='error' sx={{ ml: 1 }} />
                        )}
                        {invitation.status === 'accepted' && (
                           <Chip size='small' label='Accepted' color='success' sx={{ ml: 1 }} />
                        )}
                        {invitation.status === 'declined' && (
                           <Chip size='small' label='Declined' color='error' sx={{ ml: 1 }} />
                        )}
                     </Typography>
                  </>
               }
            />
         </ListItem>
      );
   };

   // Handle open send invitation dialog
   const handleOpenSendInvitationDialog = () => {
      setSendInvitationDialogOpen(true);
   };

   // Handle close send invitation dialog
   const handleCloseSendInvitationDialog = () => {
      setSendInvitationDialogOpen(false);
   };

   // Handle invitation sent successfully
   const handleInvitationSent = () => {
      setSendInvitationDialogOpen(false);
      setRefreshing(true);
   };

   // Add function to get invite title
   const getInviteTitle = (invitation: Invitation) => {
      if (invitation.tripTitle) {
         return `Trip: ${invitation.tripTitle}`;
      } else if (invitation.eventTitle) {
         return `Event: ${invitation.eventTitle}`;
      }
      return 'Untitled';
   };

   // Add function to navigate to the trip or event
   const handleNavigateToItem = (invitation: Invitation) => {
      if (invitation.tripId) {
         navigate(`/trip/${invitation.tripId}`);
      } else if (invitation.eventId) {
         navigate(`/events/${invitation.eventId}`);
      }
   };

   useEffect(() => {
      fetchInvitations();
   }, [sendInvitationDialogOpen]);

   return (
      <Container
         maxWidth='lg'
         sx={{
            mt: isMobile ? 1 : 3,
            mb: isMobile ? 3 : 5,
            px: isMobile ? 1 : 2,
            minHeight: '100vh',
            bgcolor: isMobile ? 'grey.50' : 'background.default',
            pb: isMobile ? 8 : 0,
         }}
      >
         <Paper
            sx={{
               p: isMobile ? 2 : 3,
               borderRadius: isMobile ? 3 : 2,
               boxShadow: isMobile ? 1 : 2,
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  mb: isMobile ? 2 : 3,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 2 : 0,
               }}
            >
               <Typography
                  variant='h5'
                  component='h1'
                  sx={{
                     fontSize: isMobile ? fontSize.h5 : fontSize.h4,
                     textAlign: isMobile ? 'center' : 'left',
                  }}
               >
                  Manage{' '}
                  {invitationType !== 'all'
                     ? invitationType === 'trips'
                        ? 'Trip'
                        : 'Event'
                     : 'Trip & Event'}{' '}
                  Invitations
               </Typography>

               <Box
                  sx={{
                     display: 'flex',
                     gap: isMobile ? 1 : 2,
                     flexDirection: isMobile ? 'column' : 'row',
                     width: isMobile ? '100%' : 'auto',
                  }}
               >
                  <Button
                     variant='contained'
                     startIcon={<PersonAddIcon />}
                     onClick={() => {
                        // if (checkSubscriptionStatus()) {
                        //    handleOpenSendInvitationDialog();
                        // }
                        handleOpenSendInvitationDialog();
                     }}
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        mr: isMobile ? 0 : 2,
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        width: isMobile ? '100%' : 'auto',
                     }}
                  >
                     Send Invitations
                  </Button>

                  <Button
                     variant='outlined'
                     startIcon={<RefreshIcon />}
                     onClick={handleRefresh}
                     disabled={refreshing || !!resendingId}
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        width: isMobile ? '100%' : 'auto',
                     }}
                  >
                     Refresh
                  </Button>
               </Box>
            </Box>

            {/* Guidance section - collapsible */}
            <Paper
               elevation={0}
               sx={{
                  mb: isMobile ? 2 : 3,
                  bgcolor: 'rgba(144, 202, 249, 0.08)',
                  border: '1px solid rgba(144, 202, 249, 0.2)',
                  borderRadius: isMobile ? 1 : 2,
                  overflow: 'hidden',
               }}
            >
               {/* Header with toggle */}
               <Box
                  sx={{
                     p: isMobile ? 1.5 : 2,
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     borderBottom: guideSectionExpanded
                        ? '1px solid rgba(144, 202, 249, 0.2)'
                        : 'none',
                     cursor: 'pointer',
                  }}
                  onClick={() => setGuideSectionExpanded(!guideSectionExpanded)}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     <InfoIcon color='primary' sx={{ mr: 1 }} />
                     <Typography
                        variant='h6'
                        color='primary'
                        sx={{
                           fontSize: isMobile ? fontSize.body1 : fontSize.h6,
                        }}
                     >
                        About Invitations & Collaboration
                     </Typography>
                  </Box>
                  <IconButton
                     size={isMobile ? 'medium' : 'small'}
                     color='primary'
                     onClick={(e) => {
                        e.stopPropagation();
                        setGuideSectionExpanded(!guideSectionExpanded);
                     }}
                     sx={{
                        minHeight: isMobile ? '40px' : '32px',
                        minWidth: isMobile ? '40px' : '32px',
                     }}
                  >
                     {guideSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
               </Box>

               {/* Collapsible content */}
               <Collapse in={guideSectionExpanded}>
                  <Box sx={{ p: isMobile ? 2 : 3, pt: isMobile ? 1 : 1 }}>
                     <Typography
                        variant='body1'
                        paragraph
                        sx={{
                           fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        }}
                     >
                        This is your central hub for managing invitations to trips and events.
                        Invite friends, family, and colleagues to collaborate on your travel
                        experiences.
                     </Typography>

                     <Grid container spacing={isMobile ? 1 : 2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                 <PhotoIcon color='error' sx={{ mr: 1 }} />
                                 <Typography
                                    variant='subtitle1'
                                    fontWeight='bold'
                                    sx={{
                                       fontSize: isMobile ? fontSize.body2 : fontSize.subtitle1,
                                    }}
                                 >
                                    Photo Collaboration
                                 </Typography>
                              </Box>
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontSize: isMobile ? fontSize.caption : fontSize.body2,
                                 }}
                              >
                                 Invited participants can upload and share photos on trips and
                                 events you've created. This creates a shared album of memories that
                                 everyone can enjoy.
                              </Typography>
                           </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                 <PeopleIcon color='success' sx={{ mr: 1 }} />
                                 <Typography
                                    variant='subtitle1'
                                    fontWeight='bold'
                                    sx={{
                                       fontSize: isMobile ? fontSize.body2 : fontSize.subtitle1,
                                    }}
                                 >
                                    Network Management
                                 </Typography>
                              </Box>
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontSize: isMobile ? fontSize.caption : fontSize.body2,
                                 }}
                              >
                                 People you invite become part of your travel network. They can be
                                 added as connections, making it easier to plan future adventures
                                 together.
                              </Typography>
                           </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                 <ShareIcon color='primary' sx={{ mr: 1 }} />
                                 <Typography
                                    variant='subtitle1'
                                    fontWeight='bold'
                                    sx={{
                                       fontSize: isMobile ? fontSize.body2 : fontSize.subtitle1,
                                    }}
                                 >
                                    Content Management
                                 </Typography>
                              </Box>
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontSize: isMobile ? fontSize.caption : fontSize.body2,
                                 }}
                              >
                                 <strong>Important:</strong> If you remove someone from a trip or
                                 event, their photos and contributions will also be removed from
                                 that experience.
                              </Typography>
                           </Box>
                        </Grid>
                     </Grid>

                     <Box
                        sx={{
                           mt: 2,
                           pt: 2,
                           borderTop: `1px dashed ${theme.palette.divider}`,
                           px: isMobile ? 1 : 0,
                        }}
                     >
                        <Typography
                           variant='body2'
                           color='text.secondary'
                           sx={{
                              fontSize: isMobile ? fontSize.caption : fontSize.body2,
                           }}
                        >
                           <strong>Pro Tip:</strong> For collaborative trips, consider adding people
                           as participants early in the planning process so they can contribute
                           ideas and photos from the start.
                        </Typography>
                     </Box>
                  </Box>
               </Collapse>
            </Paper>

            {/* Stats summary */}
            <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 2 : 3 }}>
               <Grid item xs={12} sm={4}>
                  <Paper
                     elevation={2}
                     sx={{
                        p: isMobile ? 1.5 : 2,
                        height: '100%',
                        borderRadius: isMobile ? 1 : 2,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                     }}
                  >
                     <Stack direction='row' spacing={1} alignItems='center'>
                        <Avatar
                           sx={{
                              bgcolor: 'primary.dark',
                              width: isMobile ? 32 : 40,
                              height: isMobile ? 32 : 40,
                           }}
                        >
                           <HourglassEmpty />
                        </Avatar>
                        <Box>
                           <Typography
                              variant='h5'
                              fontWeight='bold'
                              sx={{
                                 fontSize: isMobile ? fontSize.h6 : fontSize.h5,
                              }}
                           >
                              {pendingCount}
                           </Typography>
                           <Typography
                              variant='body2'
                              sx={{
                                 fontSize: isMobile ? fontSize.caption : fontSize.body2,
                              }}
                           >
                              Pending {pendingCount === 1 ? 'Invitation' : 'Invitations'}
                           </Typography>
                        </Box>
                     </Stack>
                  </Paper>
               </Grid>

               <Grid item xs={12} sm={4}>
                  <Paper
                     elevation={2}
                     sx={{
                        p: isMobile ? 1.5 : 2,
                        height: '100%',
                        borderRadius: isMobile ? 1 : 2,
                        bgcolor: theme.palette.success.main,
                        color: theme.palette.success.contrastText,
                     }}
                  >
                     <Stack direction='row' spacing={1} alignItems='center'>
                        <Avatar
                           sx={{
                              bgcolor: 'success.dark',
                              width: isMobile ? 32 : 40,
                              height: isMobile ? 32 : 40,
                           }}
                        >
                           <CheckCircleIcon />
                        </Avatar>
                        <Box>
                           <Typography
                              variant='h5'
                              fontWeight='bold'
                              sx={{
                                 fontSize: isMobile ? fontSize.h6 : fontSize.h5,
                              }}
                           >
                              {acceptedCount}
                           </Typography>
                           <Typography
                              variant='body2'
                              sx={{
                                 fontSize: isMobile ? fontSize.caption : fontSize.body2,
                              }}
                           >
                              Accepted {acceptedCount === 1 ? 'Invitation' : 'Invitations'}
                           </Typography>
                        </Box>
                     </Stack>
                  </Paper>
               </Grid>

               <Grid item xs={12} sm={4}>
                  <Paper
                     elevation={2}
                     sx={{
                        p: isMobile ? 1.5 : 2,
                        height: '100%',
                        borderRadius: isMobile ? 1 : 2,
                        bgcolor: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                     }}
                  >
                     <Stack direction='row' spacing={1} alignItems='center'>
                        <Avatar
                           sx={{
                              bgcolor: 'error.dark',
                              width: isMobile ? 32 : 40,
                              height: isMobile ? 32 : 40,
                           }}
                        >
                           <CancelIcon />
                        </Avatar>
                        <Box>
                           <Typography
                              variant='h5'
                              fontWeight='bold'
                              sx={{
                                 fontSize: isMobile ? fontSize.h6 : fontSize.h5,
                              }}
                           >
                              {declinedCount}
                           </Typography>
                           <Typography
                              variant='body2'
                              sx={{
                                 fontSize: isMobile ? fontSize.caption : fontSize.body2,
                              }}
                           >
                              Declined {declinedCount === 1 ? 'Invitation' : 'Invitations'}
                           </Typography>
                        </Box>
                     </Stack>
                  </Paper>
               </Grid>
            </Grid>

            {/* Search and filter bar */}
            <Box sx={{ mb: isMobile ? 1 : 2 }}>
               <TextField
                  fullWidth
                  placeholder='Search by email, phone or trip name'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                     startAdornment: (
                        <InputAdornment position='start'>
                           <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                     ),
                  }}
                  variant='outlined'
                  size={isMobile ? 'medium' : 'small'}
                  sx={{
                     '& .MuiInputBase-input': {
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        color: theme.palette.text.primary,
                        '&::placeholder': {
                           color: theme.palette.text.secondary,
                           opacity: 1,
                        },
                     },
                     '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                     },
                     '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                     },
                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                     },
                  }}
               />
            </Box>

            {/* Add UI for filtering by invitation type */}
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: isMobile ? 1 : 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0,
               }}
            >
               <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                     mr: isMobile ? 0 : 1,
                     fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                  }}
               >
                  Filter by type:
               </Typography>
               <Box
                  sx={{
                     display: 'flex',
                     gap: 0.5,
                     flexWrap: 'wrap',
                     justifyContent: isMobile ? 'center' : 'flex-start',
                  }}
               >
                  <Chip
                     label='All'
                     variant={invitationType === 'all' ? 'filled' : 'outlined'}
                     onClick={() => setInvitationType('all')}
                     size={isMobile ? 'small' : 'medium'}
                     sx={{
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        height: isMobile ? '24px' : '32px',
                     }}
                  />
                  <Chip
                     label='Trips'
                     variant={invitationType === 'trips' ? 'filled' : 'outlined'}
                     color='info'
                     onClick={() => setInvitationType('trips')}
                     size={isMobile ? 'small' : 'medium'}
                     sx={{
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        height: isMobile ? '24px' : '32px',
                     }}
                  />
                  <Chip
                     label='Events'
                     variant={invitationType === 'events' ? 'filled' : 'outlined'}
                     color='secondary'
                     onClick={() => setInvitationType('events')}
                     size={isMobile ? 'small' : 'medium'}
                     sx={{
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        height: isMobile ? '24px' : '32px',
                     }}
                  />
               </Box>
            </Box>

            {/* Tabs for filtering */}
            <Paper sx={{ mb: isMobile ? 2 : 3 }}>
               <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor='primary'
                  textColor='primary'
                  variant={isMobile ? 'scrollable' : 'fullWidth'}
                  scrollButtons={isMobile ? 'auto' : false}
                  aria-label='invitation tabs'
                  sx={{
                     '& .MuiTab-root': {
                        minHeight: isMobile ? '48px' : '40px',
                        fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        px: isMobile ? 1 : 2,
                     },
                  }}
               >
                  <Tab
                     label={`All (${invitations.length})`}
                     sx={{ fontSize: isMobile ? fontSize.body2 : fontSize.body1 }}
                  />
                  <Tab
                     label={`Pending (${pendingCount})`}
                     sx={{ fontSize: isMobile ? fontSize.body2 : fontSize.body1 }}
                  />
                  <Tab
                     label={`Accepted (${acceptedCount})`}
                     sx={{ fontSize: isMobile ? fontSize.body2 : fontSize.body1 }}
                  />
                  <Tab
                     label={`Declined (${declinedCount})`}
                     sx={{ fontSize: isMobile ? fontSize.body2 : fontSize.body1 }}
                  />
               </Tabs>
            </Paper>

            {/* Loading or error states */}
            {loading && !refreshing ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 3 : 5 }}>
                  <CircularProgress />
               </Box>
            ) : resendingId ? (
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'center',
                     py: isMobile ? 2 : 3,
                     alignItems: 'center',
                     gap: 1,
                  }}
               >
                  <CircularProgress size={20} />
                  <Typography variant='body2' color='text.secondary'>
                     Resending invitation...
                  </Typography>
               </Box>
            ) : error ? (
               <Box sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
                  <Typography
                     color='error'
                     align='center'
                     sx={{
                        py: 1,
                        fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                     }}
                  >
                     {error.includes('requires a database index')
                        ? 'Database index not set up yet'
                        : error}
                  </Typography>
                  {error.includes('index') && (
                     <>
                        <Typography
                           variant='body2'
                           color='text.secondary'
                           sx={{
                              mt: 1,
                              mb: 2,
                              fontSize: isMobile ? fontSize.caption : fontSize.body2,
                           }}
                        >
                           This is a one-time setup needed for the invitation tracking feature
                        </Typography>
                        <Button
                           variant='contained'
                           size={isMobile ? 'medium' : 'small'}
                           color='primary'
                           onClick={() =>
                              window.open(
                                 'https://console.firebase.google.com/project/intenerarymap/firestore/indexes',
                                 '_blank'
                              )
                           }
                           startIcon={<RefreshIcon />}
                           sx={{
                              minHeight: isMobile ? '48px' : '32px',
                              px: isMobile ? 3 : 2,
                              fontSize: isMobile ? fontSize.body1 : 'inherit',
                           }}
                        >
                           Create Index
                        </Button>
                     </>
                  )}
               </Box>
            ) : filteredInvitations.length === 0 ? (
               <Box sx={{ textAlign: 'center', py: isMobile ? 3 : 5 }}>
                  <Typography
                     variant='body1'
                     color='text.secondary'
                     sx={{
                        fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                     }}
                  >
                     {searchTerm
                        ? 'No invitations match your search'
                        : tabValue === 0
                          ? "You haven't sent any invitations yet"
                          : `No ${tabValue === 1 ? 'pending' : tabValue === 2 ? 'accepted' : 'declined'} invitations found`}
                  </Typography>
                  {!searchTerm && tabValue === 0 && (
                     <Button
                        variant='contained'
                        startIcon={<PersonAddIcon />}
                        onClick={() => {
                           if (checkSubscriptionStatus()) {
                              handleOpenSendInvitationDialog();
                           }
                        }}
                        size={isMobile ? 'large' : 'medium'}
                        sx={{
                           mt: 2,
                           minHeight: isMobile ? '48px' : '40px',
                           px: isMobile ? 3 : 2,
                           fontSize: isMobile ? fontSize.body1 : 'inherit',
                        }}
                     >
                        Invite Travel Buddies
                     </Button>
                  )}
               </Box>
            ) : (
               // Invitation list
               <List
                  sx={{
                     '& .MuiListItem-root': {
                        px: isMobile ? 1 : 2,
                     },
                  }}
               >
                  {filteredInvitations.map(renderInvitationItem)}
               </List>
            )}
         </Paper>

         {/* Menu for invitation actions */}
         <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            {selectedInvitation?.status === 'pending' && (
               <MenuItem
                  onClick={() => handleResendInvite(selectedInvitation.id)}
                  disabled={resendingId === selectedInvitation?.id}
               >
                  <ListItemIcon>
                     {resendingId === selectedInvitation?.id ? (
                        <CircularProgress size={20} />
                     ) : (
                        <SendIcon fontSize='small' />
                     )}
                  </ListItemIcon>
                  <ListItemText>Resend Invitation</ListItemText>
               </MenuItem>
            )}
            <MenuItem
               onClick={() => selectedInvitation && handleNavigateToItem(selectedInvitation)}
            >
               <ListItemIcon>
                  <ArrowForwardIcon />
               </ListItemIcon>
               <ListItemText>View Details</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
               onClick={() => {
                  handleMenuClose();
                  setOpenDeleteDialog(true);
               }}
               sx={{ color: 'error.main' }}
            >
               <ListItemIcon>
                  <DeleteIcon fontSize='small' color='error' />
               </ListItemIcon>
               <ListItemText>Delete Invitation</ListItemText>
            </MenuItem>
         </Menu>

         {/* Delete confirmation dialog */}
         <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
            <DialogTitle>Delete Invitation?</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Are you sure you want to delete this invitation to "
                  {selectedInvitation?.tripTitle || selectedInvitation?.eventTitle}"?
                  {selectedInvitation?.status === 'accepted' && (
                     <Box sx={{ mt: 2 }}>
                        <Alert severity='warning'>
                           This person has already accepted the invitation. They will still have
                           access unless you remove them from the participants.
                        </Alert>
                     </Box>
                  )}
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
               <Button onClick={handleDeleteInvite} color='error'>
                  Delete
               </Button>
            </DialogActions>
         </Dialog>

         {/* Send Invitation Dialog */}
         <SendInvitationDialog
            open={sendInvitationDialogOpen}
            onClose={handleCloseSendInvitationDialog}
            onInvitationSent={handleInvitationSent}
         />
      </Container>
   );
};

export default InvitationManagement;